import { useState } from "react";

interface FAQQuestion {
  question: string;
  answer: string;
}

interface Props {
  title?: string;
  questions: FAQQuestion[];
  useSchema?: boolean;
  className?: string;
}

export default function FAQSection({ title, questions, useSchema = true, className }: Props) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (i: number) => {
    setOpenIndex(openIndex === i ? null : i);
  };

  const faqSchema = useSchema ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": questions.map((q) => ({
      "@type": "Question",
      "name": q.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": q.answer,
      },
    })),
  } : null;

  return (
    <section className={className || "py-10 md:py-14 bg-background-100"}>
      <div className="px-4 md:px-6 max-w-3xl mx-auto">
        {useSchema && faqSchema && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
        )}
        {title && (
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6 md:mb-8 font-heading text-center">
            {title}
          </h2>
        )}
        <div className="space-y-3 md:space-y-4">
          {questions.map((q, i) => {
            const isOpen = openIndex === i;
            return (
              <div key={i} className="bg-background-50 rounded-xl border border-background-200/60 overflow-hidden">
                <button
                  onClick={() => toggle(i)}
                  className="w-full flex items-center justify-between gap-3 p-4 md:p-5 text-left cursor-pointer hover:bg-background-100/60 transition-colors"
                >
                  <h3 className="text-sm md:text-base font-bold text-foreground-900 pr-2">{q.question}</h3>
                  <span className={`w-5 h-5 flex items-center justify-center flex-shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}>
                    <i className={`ri-arrow-down-s-line text-foreground-500 text-lg`}></i>
                  </span>
                </button>
                <div
                  className={`grid transition-all duration-300 ease-in-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
                >
                  <div className="overflow-hidden">
                    <p className="text-xs md:text-sm text-foreground-600 leading-relaxed px-4 md:px-5 pb-4 md:pb-5">
                      {q.answer}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}